import { Col, Properties, Property, Row } from '../../../Common/Property';
import { CodeGroup } from '../../../Common/Code';
import Eyebrow from '../../../Common/Eyebrow';
import { Note } from '../../../Common/Note';

export default function Docs() {
  return (
    <>
      <Eyebrow label='/flight/sandbox/domestic/map' tag='POST' />
      <h1>Seat Map</h1>
      <p>
        As the name suggests, employees are a core part of Dice — the very
        reason Dice exists is so you can provide secure spends management to
        your employees. On this page, we'll dive into how to create employees
        programmatically.
      </p>
      <h2>Request Model</h2>
      <p className='lead'>
        Details of all the parameters that can be passed in the request.
      </p>
      <Row>
        <Col>
          <Properties>
            <Property name='flowId' type='string'>
              Value can be extracted from Seat Map api response.
            </Property>
            <Property name='inventoryId' type='string'>
              Value can be extracted from Seat Map api response.
            </Property>
            <Note>
              <code>flowId</code> & <code>inventoryId</code> are valid for only
              15 mins
            </Note>
          </Properties>
        </Col>
        <Col>
          <CodeGroup
            tag='POST'
            label='/flight/sandbox/domestic/map'
            title='Request Payload'
          >
            <code title='cURL'>
              curl --request POST \ <br />
              --url /flight/sandbox/domestic/map \ <br />
              --header 'Origin: Dice' \ <br />
              --header 'accept: application/json' \ <br />
              --header 'content-type: application/json' <br />
            </code>
          </CodeGroup>

          <CodeGroup title='Response Pay Load'>
            <code title='json'>
              {JSON.stringify(
                {
                  success: true,
                  app: 'Trip Jack',
                  map: [
                    {
                      id: '953',
                      seats: [
                        {
                          row: 1,
                          column: 1,
                          empty: false,
                          code: '1A',
                          amount: 1500,
                          booked: true,
                          aisle: false,
                          legroom: true,
                        },
                        {
                          row: 1,
                          column: 2,
                          empty: false,
                          code: '1B',
                          amount: 1200,
                          booked: false,
                          aisle: false,
                          legroom: true,
                        },
                        {
                          row: 1,
                          column: 3,
                          empty: false,
                          code: '1C',
                          amount: 1500,
                          booked: false,
                          aisle: true,
                          legroom: true,
                        },
                        {
                          row: 1,
                          column: 5,
                          empty: false,
                          code: '1D',
                          amount: 1500,
                          booked: false,
                          aisle: true,
                          legroom: true,
                        },
                        {
                          row: 1,
                          column: 6,
                          empty: false,
                          code: '1E',
                          amount: 1200,
                          booked: false,
                          aisle: false,
                          legroom: true,
                        },
                        {
                          row: 1,
                          column: 7,
                          empty: false,
                          code: '1F',
                          amount: 1500,
                          booked: false,
                          aisle: false,
                          legroom: true,
                        },
                        {
                          row: 2,
                          column: 1,
                          empty: false,
                          code: '2A',
                          amount: 400,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 2,
                          column: 2,
                          empty: false,
                          code: '2B',
                          amount: 400,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 2,
                          column: 3,
                          empty: false,
                          code: '2C',
                          amount: 400,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 2,
                          column: 5,
                          empty: false,
                          code: '2D',
                          amount: 400,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 2,
                          column: 6,
                          empty: false,
                          code: '2E',
                          amount: 400,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 2,
                          column: 7,
                          empty: false,
                          code: '2F',
                          amount: 400,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 3,
                          column: 1,
                          empty: false,
                          code: '3A',
                          amount: 400,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 3,
                          column: 2,
                          empty: false,
                          code: '3B',
                          amount: 400,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 3,
                          column: 3,
                          empty: false,
                          code: '3C',
                          amount: 400,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 3,
                          column: 5,
                          empty: false,
                          code: '3D',
                          amount: 400,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 3,
                          column: 6,
                          empty: false,
                          code: '3E',
                          amount: 400,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 3,
                          column: 7,
                          empty: false,
                          code: '3F',
                          amount: 400,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 4,
                          column: 1,
                          empty: false,
                          code: '4A',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 4,
                          column: 2,
                          empty: false,
                          code: '4B',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 4,
                          column: 3,
                          empty: false,
                          code: '4C',
                          amount: 350,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 4,
                          column: 5,
                          empty: false,
                          code: '4D',
                          amount: 350,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 4,
                          column: 6,
                          empty: false,
                          code: '4E',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 4,
                          column: 7,
                          empty: false,
                          code: '4F',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 5,
                          column: 1,
                          empty: false,
                          code: '5A',
                          amount: 350,
                          booked: true,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 5,
                          column: 2,
                          empty: false,
                          code: '5B',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 5,
                          column: 3,
                          empty: false,
                          code: '5C',
                          amount: 350,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 5,
                          column: 5,
                          empty: false,
                          code: '5D',
                          amount: 350,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 5,
                          column: 6,
                          empty: false,
                          code: '5E',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 5,
                          column: 7,
                          empty: false,
                          code: '5F',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 6,
                          column: 1,
                          empty: false,
                          code: '6A',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 6,
                          column: 2,
                          empty: false,
                          code: '6B',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 6,
                          column: 3,
                          empty: false,
                          code: '6C',
                          amount: 350,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 6,
                          column: 5,
                          empty: false,
                          code: '6D',
                          amount: 350,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 6,
                          column: 6,
                          empty: false,
                          code: '6E',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 6,
                          column: 7,
                          empty: false,
                          code: '6F',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 7,
                          column: 1,
                          empty: false,
                          code: '7A',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 7,
                          column: 2,
                          empty: false,
                          code: '7B',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 7,
                          column: 3,
                          empty: false,
                          code: '7C',
                          amount: 350,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 7,
                          column: 5,
                          empty: false,
                          code: '7D',
                          amount: 350,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 7,
                          column: 6,
                          empty: false,
                          code: '7E',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 7,
                          column: 7,
                          empty: false,
                          code: '7F',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 8,
                          column: 1,
                          empty: false,
                          code: '8A',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 8,
                          column: 2,
                          empty: false,
                          code: '8B',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 8,
                          column: 3,
                          empty: false,
                          code: '8C',
                          amount: 350,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 8,
                          column: 5,
                          empty: false,
                          code: '8D',
                          amount: 350,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 8,
                          column: 6,
                          empty: false,
                          code: '8E',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 8,
                          column: 7,
                          empty: false,
                          code: '8F',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 9,
                          column: 1,
                          empty: false,
                          code: '9A',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 9,
                          column: 2,
                          empty: false,
                          code: '9B',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 9,
                          column: 3,
                          empty: false,
                          code: '9C',
                          amount: 350,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 9,
                          column: 5,
                          empty: false,
                          code: '9D',
                          amount: 350,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 9,
                          column: 6,
                          empty: false,
                          code: '9E',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 9,
                          column: 7,
                          empty: false,
                          code: '9F',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 10,
                          column: 1,
                          empty: false,
                          code: '10A',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 10,
                          column: 2,
                          empty: false,
                          code: '10B',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 10,
                          column: 3,
                          empty: false,
                          code: '10C',
                          amount: 350,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 10,
                          column: 5,
                          empty: false,
                          code: '10D',
                          amount: 350,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 10,
                          column: 6,
                          empty: false,
                          code: '10E',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 10,
                          column: 7,
                          empty: false,
                          code: '10F',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 11,
                          column: 1,
                          empty: false,
                          code: '11A',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 11,
                          column: 2,
                          empty: false,
                          code: '11B',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 11,
                          column: 3,
                          empty: false,
                          code: '11C',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 11,
                          column: 5,
                          empty: false,
                          code: '11D',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 11,
                          column: 6,
                          empty: false,
                          code: '11E',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 11,
                          column: 7,
                          empty: false,
                          code: '11F',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 12,
                          column: 1,
                          empty: false,
                          code: '12A',
                          amount: 1200,
                          booked: false,
                          aisle: false,
                          legroom: true,
                        },
                        {
                          row: 12,
                          column: 2,
                          empty: false,
                          code: '12B',
                          amount: 1200,
                          booked: false,
                          aisle: false,
                          legroom: true,
                        },
                        {
                          row: 12,
                          column: 3,
                          empty: false,
                          code: '12C',
                          amount: 1200,
                          booked: false,
                          aisle: true,
                          legroom: true,
                        },
                        {
                          row: 12,
                          column: 5,
                          empty: false,
                          code: '12D',
                          amount: 1200,
                          booked: false,
                          aisle: true,
                          legroom: true,
                        },
                        {
                          row: 12,
                          column: 6,
                          empty: false,
                          code: '12E',
                          amount: 1200,
                          booked: false,
                          aisle: false,
                          legroom: true,
                        },
                        {
                          row: 12,
                          column: 7,
                          empty: false,
                          code: '12F',
                          amount: 1200,
                          booked: false,
                          aisle: false,
                          legroom: true,
                        },
                        {
                          row: 13,
                          column: 1,
                          empty: false,
                          code: '13A',
                          amount: 1200,
                          booked: false,
                          aisle: false,
                          legroom: true,
                        },
                        {
                          row: 13,
                          column: 2,
                          empty: false,
                          code: '13B',
                          amount: 1200,
                          booked: false,
                          aisle: false,
                          legroom: true,
                        },
                        {
                          row: 13,
                          column: 3,
                          empty: false,
                          code: '13C',
                          amount: 1200,
                          booked: false,
                          aisle: true,
                          legroom: true,
                        },
                        {
                          row: 13,
                          column: 5,
                          empty: false,
                          code: '13D',
                          amount: 1200,
                          booked: false,
                          aisle: true,
                          legroom: true,
                        },
                        {
                          row: 13,
                          column: 6,
                          empty: false,
                          code: '13E',
                          amount: 1200,
                          booked: false,
                          aisle: false,
                          legroom: true,
                        },
                        {
                          row: 13,
                          column: 7,
                          empty: false,
                          code: '13F',
                          amount: 1200,
                          booked: false,
                          aisle: false,
                          legroom: true,
                        },
                        {
                          row: 14,
                          column: 1,
                          empty: false,
                          code: '14A',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 14,
                          column: 2,
                          empty: false,
                          code: '14B',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 14,
                          column: 3,
                          empty: false,
                          code: '14C',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 14,
                          column: 5,
                          empty: false,
                          code: '14D',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 14,
                          column: 6,
                          empty: false,
                          code: '14E',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 14,
                          column: 7,
                          empty: false,
                          code: '14F',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 15,
                          column: 1,
                          empty: false,
                          code: '15A',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 15,
                          column: 2,
                          empty: false,
                          code: '15B',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 15,
                          column: 3,
                          empty: false,
                          code: '15C',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 15,
                          column: 5,
                          empty: false,
                          code: '15D',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 15,
                          column: 6,
                          empty: false,
                          code: '15E',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 15,
                          column: 7,
                          empty: false,
                          code: '15F',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 16,
                          column: 1,
                          empty: false,
                          code: '16A',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 16,
                          column: 2,
                          empty: false,
                          code: '16B',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 16,
                          column: 3,
                          empty: false,
                          code: '16C',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 16,
                          column: 5,
                          empty: false,
                          code: '16D',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 16,
                          column: 6,
                          empty: false,
                          code: '16E',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 16,
                          column: 7,
                          empty: false,
                          code: '16F',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 17,
                          column: 1,
                          empty: false,
                          code: '17A',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 17,
                          column: 2,
                          empty: false,
                          code: '17B',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 17,
                          column: 3,
                          empty: false,
                          code: '17C',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 17,
                          column: 5,
                          empty: false,
                          code: '17D',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 17,
                          column: 6,
                          empty: false,
                          code: '17E',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 17,
                          column: 7,
                          empty: false,
                          code: '17F',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 18,
                          column: 1,
                          empty: false,
                          code: '18A',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 18,
                          column: 2,
                          empty: false,
                          code: '18B',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 18,
                          column: 3,
                          empty: false,
                          code: '18C',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 18,
                          column: 5,
                          empty: false,
                          code: '18D',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 18,
                          column: 6,
                          empty: false,
                          code: '18E',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 18,
                          column: 7,
                          empty: false,
                          code: '18F',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 19,
                          column: 1,
                          empty: false,
                          code: '19A',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 19,
                          column: 2,
                          empty: false,
                          code: '19B',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 19,
                          column: 3,
                          empty: false,
                          code: '19C',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 19,
                          column: 5,
                          empty: false,
                          code: '19D',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 19,
                          column: 6,
                          empty: false,
                          code: '19E',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 19,
                          column: 7,
                          empty: false,
                          code: '19F',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 20,
                          column: 1,
                          empty: false,
                          code: '20A',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 20,
                          column: 2,
                          empty: false,
                          code: '20B',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 20,
                          column: 3,
                          empty: false,
                          code: '20C',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 20,
                          column: 5,
                          empty: false,
                          code: '20D',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 20,
                          column: 6,
                          empty: false,
                          code: '20E',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 20,
                          column: 7,
                          empty: false,
                          code: '20F',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 21,
                          column: 1,
                          empty: false,
                          code: '21A',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 21,
                          column: 2,
                          empty: false,
                          code: '21B',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 21,
                          column: 3,
                          empty: false,
                          code: '21C',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 21,
                          column: 5,
                          empty: false,
                          code: '21D',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 21,
                          column: 6,
                          empty: false,
                          code: '21E',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 21,
                          column: 7,
                          empty: false,
                          code: '21F',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 22,
                          column: 1,
                          empty: false,
                          code: '22A',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 22,
                          column: 2,
                          empty: false,
                          code: '22B',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 22,
                          column: 3,
                          empty: false,
                          code: '22C',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 22,
                          column: 5,
                          empty: false,
                          code: '22D',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 22,
                          column: 6,
                          empty: false,
                          code: '22E',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 22,
                          column: 7,
                          empty: false,
                          code: '22F',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 23,
                          column: 1,
                          empty: false,
                          code: '23A',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 23,
                          column: 2,
                          empty: false,
                          code: '23B',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 23,
                          column: 3,
                          empty: false,
                          code: '23C',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 23,
                          column: 5,
                          empty: false,
                          code: '23D',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 23,
                          column: 6,
                          empty: false,
                          code: '23E',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 23,
                          column: 7,
                          empty: false,
                          code: '23F',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 24,
                          column: 1,
                          empty: false,
                          code: '24A',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 24,
                          column: 2,
                          empty: false,
                          code: '24B',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 24,
                          column: 3,
                          empty: false,
                          code: '24C',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 24,
                          column: 5,
                          empty: false,
                          code: '24D',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 24,
                          column: 6,
                          empty: false,
                          code: '24E',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 24,
                          column: 7,
                          empty: false,
                          code: '24F',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 25,
                          column: 1,
                          empty: false,
                          code: '25A',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 25,
                          column: 2,
                          empty: false,
                          code: '25B',
                          amount: 0,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 25,
                          column: 3,
                          empty: false,
                          code: '25C',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 25,
                          column: 5,
                          empty: false,
                          code: '25D',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 25,
                          column: 6,
                          empty: false,
                          code: '25E',
                          amount: 0,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 25,
                          column: 7,
                          empty: false,
                          code: '25F',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 26,
                          column: 1,
                          empty: false,
                          code: '26A',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 26,
                          column: 2,
                          empty: false,
                          code: '26B',
                          amount: 0,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 26,
                          column: 3,
                          empty: false,
                          code: '26C',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 26,
                          column: 5,
                          empty: false,
                          code: '26D',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 26,
                          column: 6,
                          empty: false,
                          code: '26E',
                          amount: 0,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 26,
                          column: 7,
                          empty: false,
                          code: '26F',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 27,
                          column: 1,
                          empty: false,
                          code: '27A',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 27,
                          column: 2,
                          empty: false,
                          code: '27B',
                          amount: 0,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 27,
                          column: 3,
                          empty: false,
                          code: '27C',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 27,
                          column: 5,
                          empty: false,
                          code: '27D',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 27,
                          column: 6,
                          empty: false,
                          code: '27E',
                          amount: 0,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 27,
                          column: 7,
                          empty: false,
                          code: '27F',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 28,
                          column: 1,
                          empty: false,
                          code: '28A',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 28,
                          column: 2,
                          empty: false,
                          code: '28B',
                          amount: 0,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 28,
                          column: 3,
                          empty: false,
                          code: '28C',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 28,
                          column: 5,
                          empty: false,
                          code: '28D',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 28,
                          column: 6,
                          empty: false,
                          code: '28E',
                          amount: 0,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 28,
                          column: 7,
                          empty: false,
                          code: '28F',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 29,
                          column: 1,
                          empty: false,
                          code: '29A',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 29,
                          column: 2,
                          empty: false,
                          code: '29B',
                          amount: 0,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 29,
                          column: 3,
                          empty: false,
                          code: '29C',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 29,
                          column: 5,
                          empty: false,
                          code: '29D',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 29,
                          column: 6,
                          empty: false,
                          code: '29E',
                          amount: 0,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 29,
                          column: 7,
                          empty: false,
                          code: '29F',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 30,
                          column: 1,
                          empty: false,
                          code: '30A',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 30,
                          column: 2,
                          empty: false,
                          code: '30B',
                          amount: 0,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 30,
                          column: 3,
                          empty: false,
                          code: '30C',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 30,
                          column: 5,
                          empty: false,
                          code: '30D',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 30,
                          column: 6,
                          empty: false,
                          code: '30E',
                          amount: 0,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 30,
                          column: 7,
                          empty: false,
                          code: '30F',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                      ],
                    },
                    {
                      id: '954',
                      seats: [
                        {
                          row: 1,
                          column: 1,
                          empty: false,
                          code: '1A',
                          amount: 1500,
                          booked: true,
                          aisle: false,
                          legroom: true,
                        },
                        {
                          row: 1,
                          column: 2,
                          empty: false,
                          code: '1B',
                          amount: 1200,
                          booked: false,
                          aisle: false,
                          legroom: true,
                        },
                        {
                          row: 1,
                          column: 3,
                          empty: false,
                          code: '1C',
                          amount: 1500,
                          booked: false,
                          aisle: true,
                          legroom: true,
                        },
                        {
                          row: 1,
                          column: 5,
                          empty: false,
                          code: '1D',
                          amount: 1500,
                          booked: false,
                          aisle: true,
                          legroom: true,
                        },
                        {
                          row: 1,
                          column: 6,
                          empty: false,
                          code: '1E',
                          amount: 1200,
                          booked: false,
                          aisle: false,
                          legroom: true,
                        },
                        {
                          row: 1,
                          column: 7,
                          empty: false,
                          code: '1F',
                          amount: 1500,
                          booked: false,
                          aisle: false,
                          legroom: true,
                        },
                        {
                          row: 2,
                          column: 1,
                          empty: false,
                          code: '2A',
                          amount: 400,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 2,
                          column: 2,
                          empty: false,
                          code: '2B',
                          amount: 400,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 2,
                          column: 3,
                          empty: false,
                          code: '2C',
                          amount: 400,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 2,
                          column: 5,
                          empty: false,
                          code: '2D',
                          amount: 400,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 2,
                          column: 6,
                          empty: false,
                          code: '2E',
                          amount: 400,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 2,
                          column: 7,
                          empty: false,
                          code: '2F',
                          amount: 400,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 3,
                          column: 1,
                          empty: false,
                          code: '3A',
                          amount: 400,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 3,
                          column: 2,
                          empty: false,
                          code: '3B',
                          amount: 400,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 3,
                          column: 3,
                          empty: false,
                          code: '3C',
                          amount: 400,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 3,
                          column: 5,
                          empty: false,
                          code: '3D',
                          amount: 400,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 3,
                          column: 6,
                          empty: false,
                          code: '3E',
                          amount: 400,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 3,
                          column: 7,
                          empty: false,
                          code: '3F',
                          amount: 400,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 4,
                          column: 1,
                          empty: false,
                          code: '4A',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 4,
                          column: 2,
                          empty: false,
                          code: '4B',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 4,
                          column: 3,
                          empty: false,
                          code: '4C',
                          amount: 350,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 4,
                          column: 5,
                          empty: false,
                          code: '4D',
                          amount: 350,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 4,
                          column: 6,
                          empty: false,
                          code: '4E',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 4,
                          column: 7,
                          empty: false,
                          code: '4F',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 5,
                          column: 1,
                          empty: false,
                          code: '5A',
                          amount: 350,
                          booked: true,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 5,
                          column: 2,
                          empty: false,
                          code: '5B',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 5,
                          column: 3,
                          empty: false,
                          code: '5C',
                          amount: 350,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 5,
                          column: 5,
                          empty: false,
                          code: '5D',
                          amount: 350,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 5,
                          column: 6,
                          empty: false,
                          code: '5E',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 5,
                          column: 7,
                          empty: false,
                          code: '5F',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 6,
                          column: 1,
                          empty: false,
                          code: '6A',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 6,
                          column: 2,
                          empty: false,
                          code: '6B',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 6,
                          column: 3,
                          empty: false,
                          code: '6C',
                          amount: 350,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 6,
                          column: 5,
                          empty: false,
                          code: '6D',
                          amount: 350,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 6,
                          column: 6,
                          empty: false,
                          code: '6E',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 6,
                          column: 7,
                          empty: false,
                          code: '6F',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 7,
                          column: 1,
                          empty: false,
                          code: '7A',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 7,
                          column: 2,
                          empty: false,
                          code: '7B',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 7,
                          column: 3,
                          empty: false,
                          code: '7C',
                          amount: 350,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 7,
                          column: 5,
                          empty: false,
                          code: '7D',
                          amount: 350,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 7,
                          column: 6,
                          empty: false,
                          code: '7E',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 7,
                          column: 7,
                          empty: false,
                          code: '7F',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 8,
                          column: 1,
                          empty: false,
                          code: '8A',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 8,
                          column: 2,
                          empty: false,
                          code: '8B',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 8,
                          column: 3,
                          empty: false,
                          code: '8C',
                          amount: 350,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 8,
                          column: 5,
                          empty: false,
                          code: '8D',
                          amount: 350,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 8,
                          column: 6,
                          empty: false,
                          code: '8E',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 8,
                          column: 7,
                          empty: false,
                          code: '8F',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 9,
                          column: 1,
                          empty: false,
                          code: '9A',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 9,
                          column: 2,
                          empty: false,
                          code: '9B',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 9,
                          column: 3,
                          empty: false,
                          code: '9C',
                          amount: 350,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 9,
                          column: 5,
                          empty: false,
                          code: '9D',
                          amount: 350,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 9,
                          column: 6,
                          empty: false,
                          code: '9E',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 9,
                          column: 7,
                          empty: false,
                          code: '9F',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 10,
                          column: 1,
                          empty: false,
                          code: '10A',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 10,
                          column: 2,
                          empty: false,
                          code: '10B',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 10,
                          column: 3,
                          empty: false,
                          code: '10C',
                          amount: 350,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 10,
                          column: 5,
                          empty: false,
                          code: '10D',
                          amount: 350,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 10,
                          column: 6,
                          empty: false,
                          code: '10E',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 10,
                          column: 7,
                          empty: false,
                          code: '10F',
                          amount: 350,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 11,
                          column: 1,
                          empty: false,
                          code: '11A',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 11,
                          column: 2,
                          empty: false,
                          code: '11B',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 11,
                          column: 3,
                          empty: false,
                          code: '11C',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 11,
                          column: 5,
                          empty: false,
                          code: '11D',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 11,
                          column: 6,
                          empty: false,
                          code: '11E',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 11,
                          column: 7,
                          empty: false,
                          code: '11F',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 12,
                          column: 1,
                          empty: false,
                          code: '12A',
                          amount: 1200,
                          booked: false,
                          aisle: false,
                          legroom: true,
                        },
                        {
                          row: 12,
                          column: 2,
                          empty: false,
                          code: '12B',
                          amount: 1200,
                          booked: false,
                          aisle: false,
                          legroom: true,
                        },
                        {
                          row: 12,
                          column: 3,
                          empty: false,
                          code: '12C',
                          amount: 1200,
                          booked: false,
                          aisle: true,
                          legroom: true,
                        },
                        {
                          row: 12,
                          column: 5,
                          empty: false,
                          code: '12D',
                          amount: 1200,
                          booked: false,
                          aisle: true,
                          legroom: true,
                        },
                        {
                          row: 12,
                          column: 6,
                          empty: false,
                          code: '12E',
                          amount: 1200,
                          booked: false,
                          aisle: false,
                          legroom: true,
                        },
                        {
                          row: 12,
                          column: 7,
                          empty: false,
                          code: '12F',
                          amount: 1200,
                          booked: false,
                          aisle: false,
                          legroom: true,
                        },
                        {
                          row: 13,
                          column: 1,
                          empty: false,
                          code: '13A',
                          amount: 1200,
                          booked: false,
                          aisle: false,
                          legroom: true,
                        },
                        {
                          row: 13,
                          column: 2,
                          empty: false,
                          code: '13B',
                          amount: 1200,
                          booked: false,
                          aisle: false,
                          legroom: true,
                        },
                        {
                          row: 13,
                          column: 3,
                          empty: false,
                          code: '13C',
                          amount: 1200,
                          booked: false,
                          aisle: true,
                          legroom: true,
                        },
                        {
                          row: 13,
                          column: 5,
                          empty: false,
                          code: '13D',
                          amount: 1200,
                          booked: false,
                          aisle: true,
                          legroom: true,
                        },
                        {
                          row: 13,
                          column: 6,
                          empty: false,
                          code: '13E',
                          amount: 1200,
                          booked: false,
                          aisle: false,
                          legroom: true,
                        },
                        {
                          row: 13,
                          column: 7,
                          empty: false,
                          code: '13F',
                          amount: 1200,
                          booked: false,
                          aisle: false,
                          legroom: true,
                        },
                        {
                          row: 14,
                          column: 1,
                          empty: false,
                          code: '14A',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 14,
                          column: 2,
                          empty: false,
                          code: '14B',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 14,
                          column: 3,
                          empty: false,
                          code: '14C',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 14,
                          column: 5,
                          empty: false,
                          code: '14D',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 14,
                          column: 6,
                          empty: false,
                          code: '14E',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 14,
                          column: 7,
                          empty: false,
                          code: '14F',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 15,
                          column: 1,
                          empty: false,
                          code: '15A',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 15,
                          column: 2,
                          empty: false,
                          code: '15B',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 15,
                          column: 3,
                          empty: false,
                          code: '15C',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 15,
                          column: 5,
                          empty: false,
                          code: '15D',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 15,
                          column: 6,
                          empty: false,
                          code: '15E',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 15,
                          column: 7,
                          empty: false,
                          code: '15F',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 16,
                          column: 1,
                          empty: false,
                          code: '16A',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 16,
                          column: 2,
                          empty: false,
                          code: '16B',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 16,
                          column: 3,
                          empty: false,
                          code: '16C',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 16,
                          column: 5,
                          empty: false,
                          code: '16D',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 16,
                          column: 6,
                          empty: false,
                          code: '16E',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 16,
                          column: 7,
                          empty: false,
                          code: '16F',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 17,
                          column: 1,
                          empty: false,
                          code: '17A',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 17,
                          column: 2,
                          empty: false,
                          code: '17B',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 17,
                          column: 3,
                          empty: false,
                          code: '17C',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 17,
                          column: 5,
                          empty: false,
                          code: '17D',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 17,
                          column: 6,
                          empty: false,
                          code: '17E',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 17,
                          column: 7,
                          empty: false,
                          code: '17F',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 18,
                          column: 1,
                          empty: false,
                          code: '18A',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 18,
                          column: 2,
                          empty: false,
                          code: '18B',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 18,
                          column: 3,
                          empty: false,
                          code: '18C',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 18,
                          column: 5,
                          empty: false,
                          code: '18D',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 18,
                          column: 6,
                          empty: false,
                          code: '18E',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 18,
                          column: 7,
                          empty: false,
                          code: '18F',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 19,
                          column: 1,
                          empty: false,
                          code: '19A',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 19,
                          column: 2,
                          empty: false,
                          code: '19B',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 19,
                          column: 3,
                          empty: false,
                          code: '19C',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 19,
                          column: 5,
                          empty: false,
                          code: '19D',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 19,
                          column: 6,
                          empty: false,
                          code: '19E',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 19,
                          column: 7,
                          empty: false,
                          code: '19F',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 20,
                          column: 1,
                          empty: false,
                          code: '20A',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 20,
                          column: 2,
                          empty: false,
                          code: '20B',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 20,
                          column: 3,
                          empty: false,
                          code: '20C',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 20,
                          column: 5,
                          empty: false,
                          code: '20D',
                          amount: 250,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 20,
                          column: 6,
                          empty: false,
                          code: '20E',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 20,
                          column: 7,
                          empty: false,
                          code: '20F',
                          amount: 250,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 21,
                          column: 1,
                          empty: false,
                          code: '21A',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 21,
                          column: 2,
                          empty: false,
                          code: '21B',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 21,
                          column: 3,
                          empty: false,
                          code: '21C',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 21,
                          column: 5,
                          empty: false,
                          code: '21D',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 21,
                          column: 6,
                          empty: false,
                          code: '21E',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 21,
                          column: 7,
                          empty: false,
                          code: '21F',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 22,
                          column: 1,
                          empty: false,
                          code: '22A',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 22,
                          column: 2,
                          empty: false,
                          code: '22B',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 22,
                          column: 3,
                          empty: false,
                          code: '22C',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 22,
                          column: 5,
                          empty: false,
                          code: '22D',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 22,
                          column: 6,
                          empty: false,
                          code: '22E',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 22,
                          column: 7,
                          empty: false,
                          code: '22F',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 23,
                          column: 1,
                          empty: false,
                          code: '23A',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 23,
                          column: 2,
                          empty: false,
                          code: '23B',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 23,
                          column: 3,
                          empty: false,
                          code: '23C',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 23,
                          column: 5,
                          empty: false,
                          code: '23D',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 23,
                          column: 6,
                          empty: false,
                          code: '23E',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 23,
                          column: 7,
                          empty: false,
                          code: '23F',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 24,
                          column: 1,
                          empty: false,
                          code: '24A',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 24,
                          column: 2,
                          empty: false,
                          code: '24B',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 24,
                          column: 3,
                          empty: false,
                          code: '24C',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 24,
                          column: 5,
                          empty: false,
                          code: '24D',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 24,
                          column: 6,
                          empty: false,
                          code: '24E',
                          amount: 99,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 24,
                          column: 7,
                          empty: false,
                          code: '24F',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 25,
                          column: 1,
                          empty: false,
                          code: '25A',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 25,
                          column: 2,
                          empty: false,
                          code: '25B',
                          amount: 0,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 25,
                          column: 3,
                          empty: false,
                          code: '25C',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 25,
                          column: 5,
                          empty: false,
                          code: '25D',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 25,
                          column: 6,
                          empty: false,
                          code: '25E',
                          amount: 0,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 25,
                          column: 7,
                          empty: false,
                          code: '25F',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 26,
                          column: 1,
                          empty: false,
                          code: '26A',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 26,
                          column: 2,
                          empty: false,
                          code: '26B',
                          amount: 0,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 26,
                          column: 3,
                          empty: false,
                          code: '26C',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 26,
                          column: 5,
                          empty: false,
                          code: '26D',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 26,
                          column: 6,
                          empty: false,
                          code: '26E',
                          amount: 0,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 26,
                          column: 7,
                          empty: false,
                          code: '26F',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 27,
                          column: 1,
                          empty: false,
                          code: '27A',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 27,
                          column: 2,
                          empty: false,
                          code: '27B',
                          amount: 0,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 27,
                          column: 3,
                          empty: false,
                          code: '27C',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 27,
                          column: 5,
                          empty: false,
                          code: '27D',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 27,
                          column: 6,
                          empty: false,
                          code: '27E',
                          amount: 0,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 27,
                          column: 7,
                          empty: false,
                          code: '27F',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 28,
                          column: 1,
                          empty: false,
                          code: '28A',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 28,
                          column: 2,
                          empty: false,
                          code: '28B',
                          amount: 0,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 28,
                          column: 3,
                          empty: false,
                          code: '28C',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 28,
                          column: 5,
                          empty: false,
                          code: '28D',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 28,
                          column: 6,
                          empty: false,
                          code: '28E',
                          amount: 0,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 28,
                          column: 7,
                          empty: false,
                          code: '28F',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 29,
                          column: 1,
                          empty: false,
                          code: '29A',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 29,
                          column: 2,
                          empty: false,
                          code: '29B',
                          amount: 0,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 29,
                          column: 3,
                          empty: false,
                          code: '29C',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 29,
                          column: 5,
                          empty: false,
                          code: '29D',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 29,
                          column: 6,
                          empty: false,
                          code: '29E',
                          amount: 0,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 29,
                          column: 7,
                          empty: false,
                          code: '29F',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 30,
                          column: 1,
                          empty: false,
                          code: '30A',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 30,
                          column: 2,
                          empty: false,
                          code: '30B',
                          amount: 0,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 30,
                          column: 3,
                          empty: false,
                          code: '30C',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 30,
                          column: 5,
                          empty: false,
                          code: '30D',
                          amount: 150,
                          booked: false,
                          aisle: true,
                          legroom: false,
                        },
                        {
                          row: 30,
                          column: 6,
                          empty: false,
                          code: '30E',
                          amount: 0,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                        {
                          row: 30,
                          column: 7,
                          empty: false,
                          code: '30F',
                          amount: 150,
                          booked: false,
                          aisle: false,
                          legroom: false,
                        },
                      ],
                    },
                  ],
                },
                null,
                2
              )}
            </code>
          </CodeGroup>
        </Col>
      </Row>
    </>
  );
}
